import { AnimatePresence, motion } from "framer-motion";
import { ChevronsUpDown, ExternalLink, Quote, Sparkles } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { faqs } from "../../constants/faq";
import { useOutsideClick } from "../../hooks/use-outside-click";
import { FeatureIconContainer } from "../features/feature-icon-container";
import { Container } from "../ui/container";
import { Heading } from "../ui/heading";
import { Subheading } from "../ui/subheading";
import { Link } from "react-scroll";

export const QAndA = () => {
  const [active, setActive] = useState(null);
  const ref = useRef(null);
  const id = useId();

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") {
        setActive(false);
      }
    }

    if (active && typeof active === "object") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active]);

  useOutsideClick(ref, () => setActive(null));

  return (
    <div className="dark bg-gradient-to-tr from-fuchsia-700/10 via-slate-950/10 to-black/10 py-24">
      <Container className="flex flex-col items-center justify-center">
        <div className="relative z-20 pb-10 hidden md:block" data-aos="fade-down">
          <FeatureIconContainer className="flex justify-center items-center overflow-hidden">
            <Quote className="h-6 w-6 text-fuchsia-700" />
          </FeatureIconContainer>
          <Heading className="mt-4">Frequently Asked Questions</Heading>
          <Subheading className="text-center max-w-lg mx-auto">
            Find answers to common questions about Whizmanage. Get the
            information you need to make the most out of our platform and
            optimize your store management.
          </Subheading>
        </div>
        <div className="relative z-20 pb-10 md:hidden">
          <FeatureIconContainer className="flex justify-center items-center overflow-hidden">
            <Quote className="h-6 w-6 text-fuchsia-700" />
          </FeatureIconContainer>
          <Heading className="mt-4">Frequently Asked Questions</Heading>
          <Subheading className="text-center max-w-lg mx-auto">
            Find answers to common questions about Whizmanage. Get the
            information you need to make the most out of our platform and
            optimize your store management.
          </Subheading>
        </div>

        <AnimatePresence>
          {active && typeof active === "object" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gradient-to-br from-fuchsia-700/20 via-slate-900/20 to-black/20 h-full w-full z-10"
            />
          )}
        </AnimatePresence>
        <AnimatePresence>
          {active && typeof active === "object" ? (
            <div className="fixed inset-0  grid place-items-center z-[100]">
              <motion.div
                layoutId={`card-${active.question}-${id}`}
                ref={ref}
                className="w-[90%] max-w-[800px] h-fit max-h-[90%] flex flex-col bg-white dark:bg-neutral-900 rounded-3xl"
              >
                <div>
                  <div className="flex gap-4 items-start p-4">
                    <motion.div
                      layoutId={`image-${active.title}-${id}`}
                      className="pt-0.5"
                    >
                      {active.icon}
                    </motion.div>
                    <div className="">
                      <motion.p
                        layoutId={`title-${active.title}-${id}`}
                        className="text-neutral-600 dark:text-neutral-200 md:text-xl"
                      >
                        {active.title}
                      </motion.p>
                      <motion.h3
                        layoutId={`title-${active.question}-${id}`}
                        className="font-bold text-neutral-700 dark:text-neutral-300 md:text-xl"
                      >
                        {active.question}
                      </motion.h3>
                    </div>
                    {active.ctaLink != "" &&
                      (active.ctaLink == "internal" ? (
                        <Link
                          to={
                            typeof active.title === "string"
                              ? active.title.toLowerCase()
                              : ""
                          }
                          smooth={true}
                          delay={100}
                          duration={700}
                          className="ml-auto"
                          // offset={70}
                          // onSetActive={() => setActiveLink(item.link)}
                        >
                          <motion.div
                            onClick={() => setActive(null)}
                            layoutId={`button-${active.question}-${id}`}
                            className="cursor-pointer px-4 py-3 text-sm rounded-full font-bold bg-gradient-to-r from-fuchsia-700 via-pink-600 to-fuchsia-600 hover:!opacity-70 hover:!text-white text-white animate-pulse"
                          >
                            <ExternalLink />
                          </motion.div>
                        </Link>
                      ) : (
                        <motion.a
                          layoutId={`button-${active.question}-${id}`}
                          href={active.ctaLink}
                          target="_blank"
                          className="ml-auto px-4 py-3 text-sm rounded-full font-bold bg-gradient-to-r from-fuchsia-700 via-pink-600 to-fuchsia-600 hover:!opacity-70 hover:!text-white text-white animate-pulse"
                        >
                          <ExternalLink />
                        </motion.a>
                      ))}
                  </div>
                  <div className="pt-4 relative px-4">
                    <motion.div
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-neutral-600 text-sm lg:text-base h-fit pb-6 flex flex-col items-start gap-4 overflow-auto dark:text-neutral-400 [mask:linear-gradient(to_bottom,white,white,transparent)] [scrollbar-width:none] [-ms-overflow-style:none] [-webkit-overflow-scrolling:touch]"
                    >
                      {typeof active.content === "function"
                        ? active.content()
                        : active.content}
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          ) : null}
        </AnimatePresence>
      </Container>
      <ul className="max-w-6xl mx-auto w-full gap-4 max-md:divide-y divide-neutral-900 px-4 hidden md:block">
        {faqs.map((card, index) => (
          <motion.div
            layoutId={`card-${card.question}-${id}`}
            key={`card-${card.question}-${id}`}
            onClick={() => setActive(card)}
            className="p-4 flex flex-col md:flex-row justify-between items-center hover:bg-neutral-50 dark:hover:bg-neutral-800 md:rounded-xl cursor-pointer group"
            data-aos="flip-up"
          >
            <div className="flex items-center justify-center md:items-start gap-4 flex-col md:flex-row ">
              <motion.div
                layoutId={`image-${card.title}-${id}`}
                className="pt-1"
              >
                {card.icon}
              </motion.div>
              <div className="">
                <motion.p
                  layoutId={`question-${card.title}-${id}`}
                  className="text-neutral-600 dark:text-slate-300 group-hover:text-white text-center md:text-left text-xl"
                >
                  {card.title}
                </motion.p>
                <motion.h3
                  layoutId={`question-${card.question}-${id}`}
                  className="text-neutral-800 dark:text-slate-400 group-hover:text-slate-100 text-center md:text-left"
                >
                  {card.question}
                </motion.h3>
              </div>
            </div>
            <motion.button
              layoutId={`button-${card.question}-${id}`}
              className="px-4 py-2 text-sm rounded-full font-bold group-hover:text-fuchsia-700 text-neutral-600 mt-4 md:mt-0 max-md:hidden"
            >
              {/* {card.ctaText} */}
              <ChevronsUpDown size={24} />
            </motion.button>
          </motion.div>
        ))}
      </ul>
      <ul className="max-w-6xl mx-auto w-full gap-4 max-md:divide-y divide-neutral-900 px-4 md:hidden">
        {faqs.map((card, index) => (
          <motion.div
            layoutId={`card-${card.question}-${id}`}
            key={`card-${card.question}-${id}`}
            onClick={() => setActive(card)}
            className="p-4 flex flex-col md:flex-row justify-between items-center hover:bg-neutral-50 dark:hover:bg-neutral-800 md:rounded-xl cursor-pointer group"
          >
            <div className="flex items-center justify-center md:items-start gap-4 flex-col md:flex-row ">
              <motion.div
                layoutId={`image-${card.title}-${id}`}
                className="pt-1"
              >
                {card.icon}
              </motion.div>
              <div className="">
                <motion.p
                  layoutId={`question-${card.title}-${id}`}
                  className="text-neutral-600 dark:text-slate-300 group-hover:text-white text-center md:text-left text-xl"
                >
                  {card.title}
                </motion.p>
                <motion.h3
                  layoutId={`question-${card.question}-${id}`}
                  className="text-neutral-800 dark:text-slate-400 group-hover:text-slate-100 text-center md:text-left"
                >
                  {card.question}
                </motion.h3>
              </div>
            </div>
            <motion.button
              layoutId={`button-${card.question}-${id}`}
              className="px-4 py-2 text-sm rounded-full font-bold group-hover:text-fuchsia-700 text-neutral-600 mt-4 md:mt-0 max-md:hidden"
            >
              {/* {card.ctaText} */}
              <ChevronsUpDown size={24} />
            </motion.button>
          </motion.div>
        ))}
      </ul>
    </div>
  );
};
